import React, { useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import EmptyState from './ui/EmptyState';
import Skeleton from './ui/Skeleton';
import ConfirmDialog from './ui/ConfirmDialog';
import StateMessage from './ui/StateMessage';
import { computeSaleChargeBreakdown } from '../lib/orderCharges';
import { validateClient } from '../lib/validators';

const buildChargeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const normalizeSaleCharge = (charge = {}, { autoLabel = true } = {}) => {
  const kind = charge.kind || (String(charge.label || '').toLowerCase().includes('interna') ? 'internal' : 'external');
  const defaultLabel = kind === 'internal' ? 'Comisión interna' : 'Comisión externa';
  const label = autoLabel
    ? (charge.label && charge.label !== '' ? charge.label : defaultLabel)
    : (charge.label || defaultLabel);
  return {
    id: charge.id || buildChargeId(),
    type: charge.type || 'percent',
    value: charge.value ?? '',
    mode: charge.mode || 'on_top',
    kind,
    label,
  };
};

const cloneSaleCharges = (charges = []) => (Array.isArray(charges) ? charges.map((c) => normalizeSaleCharge(c)) : []);

const ensureClientSaleCharges = (client) => {
  if (!client) return [];
  if (Array.isArray(client.defaultSaleCharges) && client.defaultSaleCharges.length > 0) {
    return cloneSaleCharges(client.defaultSaleCharges);
  }
  if (Array.isArray(client.default_sale_charges) && client.default_sale_charges.length > 0) {
    return cloneSaleCharges(client.default_sale_charges);
  }
  const legacy = Number(client.defaultSalePercentage ?? client.default_sale_percentage ?? 0) || 0;
  if (legacy) {
    return [normalizeSaleCharge({ type: 'percent', value: legacy, kind: 'internal', label: 'Comisión interna' })];
  }
  return [];
};

const serializeSaleCharges = (charges = []) => {
  return (Array.isArray(charges) ? charges : [])
    .map((charge) => {
      const valueNum = Number(charge.value || 0) || 0;
      if (!valueNum) return null;
      const kind = charge.kind || (String(charge.label || '').toLowerCase().includes('interna') ? 'internal' : 'external');
      const label = charge.label && charge.label !== ''
        ? charge.label
        : (kind === 'internal' ? 'Comisión interna' : 'Comisión externa');
      return {
        id: charge.id || buildChargeId(),
        type: charge.type || 'percent',
        value: valueNum,
        mode: charge.mode || 'on_top',
        kind,
        label,
      };
    })
    .filter(Boolean);
};

const describeCharge = (charge) => {
  const kind = charge.kind || (String(charge.label || '').toLowerCase().includes('interna') ? 'internal' : 'external');
  const label = charge.label || (kind === 'internal' ? 'Comisión interna' : 'Comisión externa');
  const isPercent = (charge.type || 'flat') === 'percent';
  const valueNum = Number(charge.value || 0) || 0;
  return `${label} · ${isPercent ? `${valueNum}%` : `$${valueNum.toLocaleString('en-US')}`}`;
};

const Clients = ({ clients, transactions, onAddClient, onDeleteClient, onUpdateClient, isLoading = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    contact: '',
    phone: '',
    deliveryAddress: '',
    defaultSaleCharges: [],
  });

  // Estado para mostrar historial por cliente
  const [history, setHistory] = useState({ show: false, client: null });
  const [editModal, setEditModal] = useState({ show: false, client: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, clientId: null, name: '' });
  const newErrors = useMemo(() => validateClient(newClient), [newClient]);
  const editErrors = useMemo(() => validateClient(editModal?.client || {}), [editModal?.client]);

  const safeClients = useMemo(() => (Array.isArray(clients) ? clients : []), [clients]);
  const safeTransactions = useMemo(() => (Array.isArray(transactions) ? transactions : []), [transactions]);

  // Calcular estadísticas por cliente
  const clientsWithStats = useMemo(() => {
    return safeClients.map((client) => {
      const defaultSaleCharges = ensureClientSaleCharges(client);
      // Consider only non-cancelled sales for spending metrics
      const clientTransactions = safeTransactions.filter((t) => t.clientId === client.id && String(t.status) !== 'cancelled');
      const saleTransactions = clientTransactions.filter((t) => (t.type || t.transaction_type) === 'sale');
      const totalSpent = saleTransactions.reduce((sum, t) => {
        const baseAmount = Number(t.amount || 0) || 0;
        const breakdown = computeSaleChargeBreakdown(t, { baseAmount }) || {};
        const fallbackCollect = baseAmount + Number(breakdown.internal || 0) + Number(breakdown.external || 0);
        const computedCollect = Number(t.salePrice || 0) || fallbackCollect;
        const brokerFee = Number(t.brokerCommission || 0) || 0;
        return sum + (computedCollect + brokerFee);
      }, 0);
      const transactionCount = clientTransactions.length;
      const pendingDeliveries = saleTransactions.reduce((sum, t) => {
        if (t.type !== 'sale') return sum;
        const baseAmount = Number(t.amount || 0);
        const breakdown = computeSaleChargeBreakdown(t, { baseAmount }) || {};
        const fallbackCollect = baseAmount + Number(breakdown.internal || 0) + Number(breakdown.external || 0);
        const computedCollect = Number(t.salePrice || 0) || fallbackCollect;
        const brokerFee = Number(t.brokerCommission || 0) || 0;
        const expected = computedCollect + brokerFee;
        const delivered = t.deliveredAmount != null ? Number(t.deliveredAmount) : Number(t.depositAmount || 0);
        return sum + Math.max(0, expected - (delivered || 0));
      }, 0);
      const totalReceived = saleTransactions.reduce((sum, t) => {
        const received = t.deliveredAmount != null ? Number(t.deliveredAmount) : Number(t.depositAmount || 0);
        return sum + (received || 0);
      }, 0);

      return {
        ...client,
        defaultSaleCharges,
        totalSpent,
        transactionCount,
        pendingDeliveries,
        totalReceived,
      };
    });
  }, [safeClients, safeTransactions]);

  const filteredClients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return clientsWithStats;
    return clientsWithStats.filter((client) => {
      const haystack = [
        client.name,
        client.contact,
        client.phone,
        client.deliveryAddress,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [clientsWithStats, searchTerm]);

  const totalTransactionCount = useMemo(
    () => clientsWithStats.reduce((sum, client) => sum + (client.transactionCount || 0), 0),
    [clientsWithStats]
  );

  const historyClientId = history?.client?.id;
  const historyTransactions = useMemo(() => {
    if (!historyClientId) return [];
    return safeTransactions.filter((t) => t.clientId === historyClientId);
  }, [historyClientId, safeTransactions]);

  const resetConfirmDialog = () => setConfirmDialog({ open: false, clientId: null, name: '' });

  const handleDeleteRequest = (client) => {
    setConfirmDialog({ open: true, clientId: client.id, name: client.name || '' });
  };

  const handleConfirmDelete = () => {
    if (confirmDialog.clientId && typeof onDeleteClient === 'function') {
      onDeleteClient(confirmDialog.clientId);
    }
    resetConfirmDialog();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewClient(prev => ({ ...prev, [name]: value }));
  };

  const updateNewClientCharge = (index, patch) => {
    setNewClient(prev => {
      const current = Array.isArray(prev.defaultSaleCharges) ? prev.defaultSaleCharges : [];
      const next = current.map((charge, idx) => {
        if (idx !== index) return charge;
        const nextCharge = normalizeSaleCharge({ ...charge, ...patch }, { autoLabel: false });
        if (patch.kind && !patch.label) {
          nextCharge.label = patch.kind === 'internal' ? 'Comisión interna' : 'Comisión externa';
        }
        return nextCharge;
      });
      return { ...prev, defaultSaleCharges: next };
    });
  };

  const handleAddNewCharge = () => {
    setNewClient(prev => {
      const current = Array.isArray(prev.defaultSaleCharges) ? prev.defaultSaleCharges : [];
      return {
        ...prev,
        defaultSaleCharges: [
          ...current,
          normalizeSaleCharge({
            type: 'percent',
            value: '',
            kind: current.length === 0 ? 'internal' : 'external',
            label: current.length === 0 ? 'Comisión interna' : 'Comisión externa',
          }, { autoLabel: false }),
        ],
      };
    });
  };

  const handleRemoveNewCharge = (index) => {
    setNewClient(prev => {
      const current = Array.isArray(prev.defaultSaleCharges) ? prev.defaultSaleCharges : [];
      const next = current.filter((_, idx) => idx !== index);
      return { ...prev, defaultSaleCharges: next };
    });
  };

  const updateEditClientCharge = (index, patch) => {
    setEditModal(prev => {
      if (!prev?.client) return prev;
      const current = Array.isArray(prev.client.defaultSaleCharges) ? prev.client.defaultSaleCharges : [];
      const next = current.map((charge, idx) => {
        if (idx !== index) return charge;
        const nextCharge = normalizeSaleCharge({ ...charge, ...patch }, { autoLabel: false });
        if (patch.kind && !patch.label) {
          nextCharge.label = patch.kind === 'internal' ? 'Comisión interna' : 'Comisión externa';
        }
        return nextCharge;
      });
      return {
        ...prev,
        client: {
          ...prev.client,
          defaultSaleCharges: next,
        },
      };
    });
  };

  const handleAddEditCharge = () => {
    setEditModal(prev => {
      if (!prev?.client) return prev;
      const current = Array.isArray(prev.client.defaultSaleCharges) ? prev.client.defaultSaleCharges : [];
      const nextCharge = normalizeSaleCharge({
        type: 'percent',
        value: '',
        kind: current.length === 0 ? 'internal' : 'external',
        label: current.length === 0 ? 'Comisión interna' : 'Comisión externa',
      }, { autoLabel: false });
      return {
        ...prev,
        client: {
          ...prev.client,
          defaultSaleCharges: [...current, nextCharge],
        },
      };
    });
  };

  const handleRemoveEditCharge = (index) => {
    setEditModal(prev => {
      if (!prev?.client) return prev;
      const current = Array.isArray(prev.client.defaultSaleCharges) ? prev.client.defaultSaleCharges : [];
      const next = current.filter((_, idx) => idx !== index);
      return {
        ...prev,
        client: {
          ...prev.client,
          defaultSaleCharges: next,
        },
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Array.isArray(newErrors) && newErrors.length > 0) return;
    if (isSubmitting) return;

    // Call the parent handler if provided to persist the new client in App state
    if (onAddClient) {
      setIsSubmitting(true);
      try {
        await onAddClient({
          ...newClient,
          defaultSaleCharges: serializeSaleCharges(newClient.defaultSaleCharges),
        });
        // Only close modal and reset if successful
        setShowModal(false);
        setNewClient({
          name: '',
          contact: '',
          phone: '',
          deliveryAddress: '',
          defaultSaleCharges: [],
        });
      } catch (error) {
        // Error notification is handled by App.js
        console.error('Failed to add client:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      console.log("Nuevo Cliente:", newClient);
      setShowModal(false);
      setNewClient({
        name: '',
        contact: '',
        phone: '',
        deliveryAddress: '',
        defaultSaleCharges: [],
      });
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (Array.isArray(editErrors) && editErrors.length > 0) return;
    if (isEditSubmitting) return;
    if (!onUpdateClient) return;

    setIsEditSubmitting(true);
    try {
      const payload = {
        ...editModal.client,
        defaultSaleCharges: serializeSaleCharges(editModal.client?.defaultSaleCharges),
      };
      delete payload.defaultSalePercentage;
      delete payload.default_sale_percentage;

      await onUpdateClient(payload);
      // Only close modal if successful
      setEditModal({ show: false, client: null });
    } catch (error) {
      // Error notification is handled by App.js
      console.error('Failed to update client:', error);
    } finally {
      setIsEditSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Clientes</h1>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Cliente
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6  border border-gray-200 dark:border-gray-700 mb-6 space-y-4 neon-border">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-300" />
          <input
            type="text"
            placeholder="Buscar por nombre, contacto o dirección"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="pl-10 pr-10 py-2 w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Limpiar búsqueda"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {!isLoading && (
          <StateMessage
            intent={searchTerm.trim() && filteredClients.length === 0 ? 'error' : 'info'}
            title={searchTerm.trim() ? 'Resultados de la búsqueda' : 'Resumen de clientes'}
            description={searchTerm.trim()
              ? filteredClients.length > 0
                ? `Encontramos ${filteredClients.length} ${filteredClients.length === 1 ? 'cliente' : 'clientes'} que coinciden con "${searchTerm}".`
                : `No encontramos clientes que coincidan con "${searchTerm}". Intenta ajustar tu búsqueda.`
              : `Gestionas ${clientsWithStats.length} ${clientsWithStats.length === 1 ? 'cliente activo' : 'clientes activos'} con ${totalTransactionCount} transacciones registradas.`}
            className="border-dashed"
          />
        )}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl  border border-gray-200 dark:border-gray-700 overflow-hidden neon-border">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Contacto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Dirección Entrega</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cargos por Defecto</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Transacciones</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Total Gastado</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Efectivo Disponible</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Saldo Disponible</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Pendiente por entregar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {isLoading && (
              Array.from({ length: 6 }).map((_, index) => (
                <tr key={`clients-loading-${index}`} className="border-b border-gray-100 dark:border-gray-700/60">
                  <td colSpan="10" className="px-6 py-4">
                    <Skeleton className="h-12 w-full" />
                  </td>
                </tr>
              ))
            )}

            {!isLoading && filteredClients.map((client) => (
              <tr key={client.id || client.name} className="hover:bg-gray-50 hover:dark:bg-gray-700/60">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{client.name}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 dark:text-gray-200">{client.contact}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{client.phone}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{client.deliveryAddress || 'N/A'}</td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">
                  {client.defaultSaleCharges && client.defaultSaleCharges.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {client.defaultSaleCharges.map((charge) => (
                        <span
                          key={charge.id}
                          className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700  dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                        >
                          {describeCharge(charge)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400 dark:text-gray-500">Sin cargos</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200">{client.transactionCount}</td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">${client.totalSpent.toLocaleString('en-US')}</td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">${Number(client.totalReceived || 0).toLocaleString('en-US')}</td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">${Number(client.balance || 0).toLocaleString('en-US')}</td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">${Number(client.pendingDeliveries || 0).toLocaleString('en-US')}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setHistory({ show: true, client })} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:dark:text-blue-300 text-sm font-medium">
                      Ver historial
                    </button>
                    <button
                      onClick={() => setEditModal({
                        show: true,
                        client: {
                          ...client,
                          defaultSaleCharges: ensureClientSaleCharges(client),
                        },
                      })}
                      className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 hover:dark:text-emerald-300 text-sm font-medium"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteRequest(client)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 hover:dark:text-red-300 text-sm font-medium"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!isLoading && filteredClients.length === 0 && (
              <tr>
                <td colSpan="10" className="px-6 py-10">
                  <EmptyState
                    title={searchTerm.trim() ? 'No se encontraron coincidencias' : 'Aún no tienes clientes registrados'}
                    description={searchTerm.trim()
                      ? 'Prueba con otro término de búsqueda o revisa la ortografía.'
                      : 'Agrega tu primer cliente para comenzar a registrar transacciones y gestionar tu cartera.'}
                    action={
                      <button
                        type="button"
                        onClick={() => setShowModal(true)}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white  transition hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4" />
                        Nuevo cliente
                      </button>
                    }
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal historial por cliente */}
      {history?.show && history.client && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Historial - {history.client.name}</h3>
              <button onClick={() => setHistory({ show: false, client: null })} className="p-2 hover:bg-gray-100 hover:dark:bg-gray-600 rounded-lg">
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {historyTransactions.length > 0 ? (
                historyTransactions.map((t, idx) => {
                  const amount = Number(t.amount || 0);
                  const isSale = t.type === 'sale';
                  const breakdown = isSale ? (computeSaleChargeBreakdown(t, { baseAmount: amount }) || {}) : {};
                  const fallbackCollect = amount + Number(breakdown.internal || 0) + Number(breakdown.external || 0);
                  const computedCollect = Number(t.salePrice || 0) || fallbackCollect;
                  const brokerFee = Number(t.brokerCommission || 0) || 0;
                  const expected = isSale ? computedCollect + brokerFee : amount;
                  const delivered = Number(
                    t.deliveredAmount !== undefined && t.deliveredAmount !== null && t.deliveredAmount !== ''
                      ? t.deliveredAmount
                      : t.depositAmount || 0
                  );
                  const pending = isSale ? Math.max(0, expected - delivered) : 0;

                  return (
                    <div key={`${t.id}-${idx}`} className="p-3 border border-gray-100 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                      <div className="text-xs text-gray-500 dark:text-gray-300">{t.date}</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.type === 'sale' ? 'Venta' : 'Pedido'} • {t.date || 'Sin fecha'}</div>
                      <div className="text-sm text-gray-700 dark:text-gray-200">Monto: ${amount.toLocaleString('en-US')}</div>
                      {isSale && (
                        <div className="text-sm text-gray-700 dark:text-gray-200">Pendiente: ${pending.toLocaleString('en-US')}</div>
                      )}
                    </div>
                  );
                })
              ) : (
                <EmptyState
                  title="Sin transacciones registradas"
                  description="Este cliente aún no tiene movimientos asociados en el historial."
                  className="border-none bg-transparent p-6"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal editar cliente con actividad */}
      {editModal?.show && editModal.client && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Editar cliente - {editModal.client.name}</h3>
              <button onClick={() => setEditModal({ show: false, client: null })} className="p-2 hover:bg-gray-100 hover:dark:bg-gray-700 rounded-lg">
                <X className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
            <form
              className="space-y-3"
              onSubmit={handleEditSubmit}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
                <input
                  type="text"
                  value={editModal.client.name || ''}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, client: { ...prev.client, name: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contacto</label>
                <input
                  type="text"
                  value={editModal.client.contact || ''}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, client: { ...prev.client, contact: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={editModal.client.phone || ''}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, client: { ...prev.client, phone: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección de entrega</label>
                <input
                  type="text"
                  value={editModal.client.deliveryAddress || ''}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, client: { ...prev.client, deliveryAddress: e.target.value } }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Cargos sugeridos para ventas</label>
                  <button
                    type="button"
                    onClick={handleAddEditCharge}
                    className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:border-gray-400 hover:text-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-400"
                  >
                    <Plus className="h-3 w-3" /> Agregar cargo
                  </button>
                </div>
                <div className="space-y-3">
                  {(editModal.client.defaultSaleCharges || []).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Configura cargos por defecto para ventas. Se aplicarán automáticamente al crear una nueva operación con este cliente.
                    </p>
                  )}
                  {(editModal.client.defaultSaleCharges || []).map((charge, index) => {
                    const chargeKind = charge.kind || 'internal';
                    const chargeType = charge.type || 'percent';
                    const valueLabel = chargeType === 'percent' ? 'Valor (%)' : 'Monto ($)';

                    return (
                      <div
                        key={charge.id || `${index}-${charge.label}`}
                        className="grid grid-cols-12 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/40"
                      >
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Destino</label>
                          <select
                            value={chargeKind}
                            onChange={(e) => updateEditClientCharge(index, { kind: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          >
                            <option value="internal">Comisión interna</option>
                            <option value="external">Comisión externa</option>
                          </select>
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Tipo</label>
                          <select
                            value={chargeType}
                            onChange={(e) => updateEditClientCharge(index, { type: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          >
                            <option value="percent">Porcentaje</option>
                            <option value="flat">Monto fijo</option>
                          </select>
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{valueLabel}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={charge.value ?? ''}
                            onChange={(e) => updateEditClientCharge(index, { value: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          />
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Etiqueta</label>
                          <input
                            type="text"
                            value={charge.label || ''}
                            onChange={(e) => updateEditClientCharge(index, { label: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Ej: Comisión interna"
                          />
                        </div>
                        <div className="col-span-12 flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveEditCharge(index)}
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                          >
                            <X className="h-3 w-3" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={(Array.isArray(editErrors) && editErrors.length > 0) || isEditSubmitting}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEditSubmitting ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Actividad reciente</h4>
              {safeTransactions.filter((t) => t.clientId === editModal.client.id).slice(0, 50).map((t, idx) => {
                const amount = Number(t.amount || 0);
                const isSale = t.type === 'sale';
                const breakdown = isSale ? (computeSaleChargeBreakdown(t, { baseAmount: amount }) || {}) : {};
                const fallbackCollect = amount + Number(breakdown.internal || 0) + Number(breakdown.external || 0);
                const computedCollect = Number(t.salePrice || 0) || fallbackCollect;
                const brokerFee = Number(t.brokerCommission || 0) || 0;
                const expected = isSale ? computedCollect + brokerFee : amount;
                const delivered = Number(t.deliveredAmount ?? t.depositAmount ?? 0);
                const pending = isSale ? Math.max(0, expected - delivered) : 0;
                return (
                  <div key={`${t.id}-${idx}`} className="p-3 border border-gray-100 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.type === 'sale' ? 'Venta' : 'Pedido'} • {t.date || 'Sin fecha'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-300">{t.date || ''}</div>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-200">Monto: ${amount.toLocaleString('en-US')}</div>
                    {t.type === 'sale' && (
                      <div className="text-xs text-gray-600 dark:text-gray-300">Pendiente: ${pending.toLocaleString('en-US')}</div>
                    )}
                  </div>
                );
              })}
              {safeTransactions.filter((t) => t.clientId === editModal.client.id).length === 0 && (
                <EmptyState
                  title="Sin actividad"
                  description="Este cliente no tiene transacciones registradas."
                  className="border-none bg-transparent p-6"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-8 w-full max-w-md relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:dark:text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6">Agregar Nuevo Cliente</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre del Cliente</label>
                <input
                  type="text"
                  name="name"
                  value={newClient.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Cliente Corporativo Z"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contacto Principal</label>
                <input
                  type="text"
                  name="contact"
                  value={newClient.contact}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Ana García"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
                <input
                  type="text"
                  name="phone"
                  value={newClient.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: 555-1234567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dirección de Entrega</label>
                <input
                  type="text"
                  name="deliveryAddress"
                  value={newClient.deliveryAddress}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Calle Falsa 123, Ciudad"
                />
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Cargos sugeridos para ventas</label>
                  <button
                    type="button"
                    onClick={handleAddNewCharge}
                    className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 transition hover:border-gray-400 hover:text-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-400"
                  >
                    <Plus className="h-3 w-3" /> Agregar cargo
                  </button>
                </div>
                <div className="space-y-3">
                  {(newClient.defaultSaleCharges || []).length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Define cargos por defecto que se aplicarán automáticamente al crear ventas con este cliente.
                    </p>
                  )}
                  {(newClient.defaultSaleCharges || []).map((charge, index) => {
                    const chargeKind = charge.kind || 'internal';
                    const chargeType = charge.type || 'percent';
                    const valueLabel = chargeType === 'percent' ? 'Valor (%)' : 'Monto ($)';

                    return (
                      <div
                        key={charge.id || `${index}-${charge.label}`}
                        className="grid grid-cols-12 gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-700/40"
                      >
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Destino</label>
                          <select
                            value={chargeKind}
                            onChange={(e) => updateNewClientCharge(index, { kind: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          >
                            <option value="internal">Comisión interna</option>
                            <option value="external">Comisión externa</option>
                          </select>
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Tipo</label>
                          <select
                            value={chargeType}
                            onChange={(e) => updateNewClientCharge(index, { type: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          >
                            <option value="percent">Porcentaje</option>
                            <option value="flat">Monto fijo</option>
                          </select>
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">{valueLabel}</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={charge.value ?? ''}
                            onChange={(e) => updateNewClientCharge(index, { value: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                          />
                        </div>
                        <div className="col-span-12 sm:col-span-3">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Etiqueta</label>
                          <input
                            type="text"
                            value={charge.label || ''}
                            onChange={(e) => updateNewClientCharge(index, { label: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700  focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                            placeholder="Ej: Comisión interna"
                          />
                        </div>
                        <div className="col-span-12 flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleRemoveNewCharge(index)}
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-1 text-xs font-medium text-rose-600 transition hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                          >
                            <X className="h-3 w-3" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {Array.isArray(newErrors) && newErrors.length > 0 && (
                <div className="text-xs text-amber-700 dark:text-amber-300">{newErrors[0]}</div>
              )}
              <button
                type="submit"
                disabled={(Array.isArray(newErrors) && newErrors.length > 0) || isSubmitting}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Agregando...' : 'Agregar Cliente'}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDialog.open}
        title="Eliminar cliente"
        description={`¿Deseas eliminar a ${confirmDialog.name || 'este cliente'}? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        intent="warning"
        onConfirm={handleConfirmDelete}
        onCancel={resetConfirmDialog}
      />
    </div>
  );
};

export default Clients;
